import { Contract, ethers } from 'ethers'
import { simpleRpcProvider } from 'utils/providers'
import { poolsConfig } from 'config/constants'
import { PoolCategory } from 'config/constants/types'
import tokens from 'config/constants/tokens'

// Addresses
import {
  getAddress,
  getPancakeProfileAddress,
  getPancakeRabbitsAddress,
  getBunnyFactoryAddress,
  getBunnySpecialAddress,
  getLotteryV2Address,
  getCraftsmanAddress,
  getCraftsmanV2Address,
  getPointCenterIfoAddress,
  getClaimRefundAddress,
  getTradingCompetitionAddress,
  getEasterNftAddress,
  getVvsVaultAddress,
  getPredictionsAddress,
  getChainlinkOracleAddress,
  getMulticallAddress,
  getBunnySpecialCakeVaultAddress,
  getBunnySpecialPredictionAddress,
  getBunnySpecialLotteryAddress,
  getFarmAuctionAddress,
  getConditionalLiquidityAddress,
  getTradingBattleAddress,
  getVvsBarAddress,
  getVvsBoostAddress,
  getZapEstimatorAddress,
  getVvsMinerMoleAddress,
  getVvsMinerMoleNurseryAddress,
  getVvsFarmBoostAddress,
  getArgoNftAddress,
} from 'utils/addressHelpers'

// ABI
import profileABI from 'config/abi/pancakeProfile.json'
import pancakeRabbitsAbi from 'config/abi/pancakeRabbits.json'
import bunnyFactoryAbi from 'config/abi/bunnyFactory.json'
import bunnySpecialAbi from 'config/abi/bunnySpecial.json'
import crc20Abi from 'config/abi/erc20.json'
import erc721Abi from 'config/abi/erc721.json'
import lpTokenAbi from 'config/abi/lpToken.json'
import vvsAbi from 'config/abi/vvs.json'
import ifoV1Abi from 'config/abi/ifoV1.json'
import ifoV2Abi from 'config/abi/ifoV2.json'
import igoAbi from 'config/abi/igo.json'
import igoV2Abi from 'config/abi/igoV2.json'
import igoV2RefundAbi from 'config/abi/igoV2Refund.json'
import igoVestingAbi from 'config/abi/igoVesting.json'
import pointCenterIfo from 'config/abi/pointCenterIfo.json'
import lotteryV2Abi from 'config/abi/lotteryV2.json'
import craftsmanABI from 'config/abi/craftsman.json'
import craftsmanV2ABI from 'config/abi/craftsmanV2.json'
import rewarderABI from 'config/abi/rewarder.json'
import rewarderV2ABI from 'config/abi/rewarderV2.json'
import sousChef from 'config/abi/sousChef.json'
import sousChefV2 from 'config/abi/sousChefV2.json'
import sousChefCro from 'config/abi/sousChefCro.json'
import claimRefundAbi from 'config/abi/claimRefund.json'
import tradingCompetitionAbi from 'config/abi/tradingCompetition.json'
import easterNftAbi from 'config/abi/easterNft.json'
import vvsVaultAbi from 'config/abi/vvsVault.json'
import predictionsAbi from 'config/abi/predictions.json'
import chainlinkOracleAbi from 'config/abi/chainlinkOracle.json'
import MultiCallAbi from 'config/abi/Multicall.json'
import bunnySpecialCakeVaultAbi from 'config/abi/bunnySpecialCakeVault.json'
import bunnySpecialPredictionAbi from 'config/abi/bunnySpecialPrediction.json'
import bunnySpecialLotteryAbi from 'config/abi/bunnySpecialLottery.json'
import farmAuctionAbi from 'config/abi/farmAuction.json'
import conditionalLiquidityAbi from 'config/abi/conditionalLiquidity.json'
import tradingBattleAbi from 'config/abi/tradingBattle.json'
import vvsBarAbi from 'config/abi/vvsBar.json'
import vvsBoostAbi from 'config/abi/vvsBoost.json'
import vvsMinerMoleAbi from 'config/abi/vvsMinerMole.json'
import vvsMinerMoleNursery from 'config/abi/vvsMinerMoleNursery.json'
import vvsFarmBoostAbi from 'config/abi/vvsFarmBoost.json'
import argoNftAbi from 'config/abi/argoNft.json'
import zapEstimatorAbi from 'config/abi/vvsZapEstimator.json'
import { ChainLinkOracleContract, FarmAuctionContract, PancakeProfileContract, PredictionsContract } from './types'
import {
  Erc20,
  Erc721,
  LpToken,
  VvsBoost,
  Vvs,
  Craftsman,
  CraftsmanV2,
  VvsMinerMole,
  VvsMinerMoleNursery,
  VvsFarmBoost,
  ArgoNft,
  Rewarder,
  RewarderV2,
} from '../../types/ethers-contracts'
import { IgoV2Refund } from '../../types/ethers-contracts/IgoV2Refund'

export const getContract = <T extends Contract>(
  abi: any,
  address: string,
  signer?: ethers.Signer | ethers.providers.Provider,
) => {
  const signerOrProvider = signer ?? simpleRpcProvider
  return new ethers.Contract(address, abi, signerOrProvider) as T
}

export const getCrc20Contract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<Erc20>(crc20Abi, address, signer)
}
export const getErc721Contract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<Erc721>(erc721Abi, address, signer)
}
export const getLpContract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<LpToken>(lpTokenAbi, address, signer)
}
export const getIfoV1Contract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(ifoV1Abi, address, signer)
}
export const getIfoV2Contract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(ifoV2Abi, address, signer)
}
export const getIgoContract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(igoAbi, address, signer)
}
export const getIgoV2Contract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(igoV2Abi, address, signer)
}
export const getIgoV2RefundContract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<IgoV2Refund>(igoV2RefundAbi, address, signer)
}
export const getIgoVestingContract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(igoVestingAbi, address, signer)
}
export const getSouschefContract = (id: number, signer?: ethers.Signer | ethers.providers.Provider) => {
  const config = poolsConfig.find((pool) => pool.sousId === id)
  const abi = config.poolCategory === PoolCategory.BINANCE ? sousChefCro : sousChef
  return getContract(abi, getAddress(config.contractAddress), signer)
}
export const getSouschefV2Contract = (id: number, signer?: ethers.Signer | ethers.providers.Provider) => {
  const config = poolsConfig.find((pool) => pool.sousId === id)
  return getContract(sousChefV2, getAddress(config.contractAddress), signer)
}
export const getPointCenterIfoContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(pointCenterIfo, getPointCenterIfoAddress(), signer)
}
export const getVvsContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<Vvs>(vvsAbi, tokens.vvs.address, signer)
}
export const getProfileContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(profileABI, getPancakeProfileAddress(), signer) as PancakeProfileContract
}
export const getPancakeRabbitContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(pancakeRabbitsAbi, getPancakeRabbitsAddress(), signer)
}
export const getBunnyFactoryContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(bunnyFactoryAbi, getBunnyFactoryAddress(), signer)
}
export const getBunnySpecialContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(bunnySpecialAbi, getBunnySpecialAddress(), signer)
}
export const getLotteryV2Contract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(lotteryV2Abi, getLotteryV2Address(), signer)
}
export const getCraftsmanContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<Craftsman>(craftsmanABI, getCraftsmanAddress(), signer)
}
export const getCraftsmanV2Contract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<CraftsmanV2>(craftsmanV2ABI, getCraftsmanV2Address(), signer)
}
export const getRewarderContract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<Rewarder>(rewarderABI, address, signer)
}
export const getClaimRefundContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(claimRefundAbi, getClaimRefundAddress(), signer)
}
export const getTradingCompetitionContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(tradingCompetitionAbi, getTradingCompetitionAddress(), signer)
}
export const getEasterNftContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(easterNftAbi, getEasterNftAddress(), signer)
}
export const getVvsVaultContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(vvsVaultAbi, getVvsVaultAddress(), signer)
}

export const getPredictionsContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(predictionsAbi, getPredictionsAddress(), signer) as PredictionsContract
}

export const getChainlinkOracleContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(chainlinkOracleAbi, getChainlinkOracleAddress(), signer) as ChainLinkOracleContract
}
export const getMulticallContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(MultiCallAbi, getMulticallAddress(), signer)
}
export const getBunnySpecialCakeVaultContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(bunnySpecialCakeVaultAbi, getBunnySpecialCakeVaultAddress(), signer)
}
export const getBunnySpecialPredictionContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(bunnySpecialPredictionAbi, getBunnySpecialPredictionAddress(), signer)
}
export const getBunnySpecialLotteryContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(bunnySpecialLotteryAbi, getBunnySpecialLotteryAddress(), signer)
}
export const getFarmAuctionContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(farmAuctionAbi, getFarmAuctionAddress(), signer) as FarmAuctionContract
}
export const getConditionalLiquidityContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(conditionalLiquidityAbi, getConditionalLiquidityAddress(), signer)
}
export const getTradingBattleContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(tradingBattleAbi, getTradingBattleAddress(), signer)
}
export const getVvsBarContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<VvsBoost>(vvsBarAbi, getVvsBarAddress(), signer)
}
export const getVvsBoostContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(vvsBoostAbi, getVvsBoostAddress(), signer)
}
export const getZapEstimatorContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract(zapEstimatorAbi, getZapEstimatorAddress(), signer)
}
export const getVvsMinerMoleContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<VvsMinerMole>(vvsMinerMoleAbi, getVvsMinerMoleAddress(), signer)
}
export const getVvsMinerMoleNurseryContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<VvsMinerMoleNursery>(vvsMinerMoleNursery, getVvsMinerMoleNurseryAddress(), signer)
}
export const getVvsBoostFarmContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<VvsFarmBoost>(vvsFarmBoostAbi, getVvsFarmBoostAddress(), signer)
}
export const getRewarderV2Contract = (address: string, signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<RewarderV2>(rewarderV2ABI, address, signer)
}
export const getArgoNftContract = (signer?: ethers.Signer | ethers.providers.Provider) => {
  return getContract<ArgoNft>(argoNftAbi, getArgoNftAddress(), signer)
}
